import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Minus, Send, Loader2, Trash2, Bot, UserRound, History, Plus } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { ChatHistoryList } from './AIChatHistory';
import { ProductContextBar } from './AIChatProductContext';
import { AIFab, useAIConfig } from './AIFab';
import { AIAvatar, ChatContacts } from './AIChatFooter';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { ProductCards, BookingCard, HandoffCard, ExtrasCard, clearDrafts } from './AIChatCards';

const API = process.env.REACT_APP_BACKEND_URL;
const SID_KEY = 'kidsevent_ai_sid';
const MSG_KEY = 'kidsevent_ai_msgs';

const fmtList = (t) => {
  let s = t || '';
  if (!(/(^|\s)1\.\s+\S/.test(s) && /\s2\.\s+[A-ZÄÖÜ„"*]/.test(s))) return s;
  s = s.replace(/\s+(?=[1-9]\.\s+[A-ZÄÖÜ„"*])/g, '\n\n').replace(/^([1-9]\.)\s+(?!\*\*)([^\n:–]{3,80}?)(\s+[–-]\s+|:\s*)/gm, '$1 **$2**\n');
  return s.replace(/^([1-9]\.[^\n]*\n[^\n]*?[.!])\s+([^\n.!?]*\?)\s*$/m, '$1\n\n$2');
};
const esc = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const fmtNames = (t, names) => {
  if (!names?.length || /(^|\n)[1-9]\.\s/.test(t)) return t;
  let s = t;
  names.forEach((n) => { s = s.replace(new RegExp(`[„"“]?(?<!\\*\\*)${esc(n)}[“"”]?(?!\\*\\*)`), `**${n}**`); });
  if (names.length < 2) return s;
  let seen = 0;
  return (s.match(/[^.!?\n]+[.!?]*\s*|\n+/g) || [s]).map((seg) => {
    if (names.some((n) => seg.includes(`**${n}**`))) { seen += 1; if (seen > 1) return `\n\n${seg.trimStart()}`; }
    else if (seen > 1 && seg.trim().endsWith('?')) { seen = -99; return `\n\n${seg.trimStart()}`; }
    return seg;
  }).join('').replace(/\n{3,}/g, '\n\n');
};
const md = (t, names) => fmtNames(fmtList(t), names).split('\n').map((line, i) => (
  <p key={i} className={line.trim() ? '' : 'h-2'}>
    {line.split(/(\*\*[^*]+\*\*)/g).map((part, j) => (part.startsWith('**') ? <strong key={j}>{part.slice(2, -2)}</strong> : part))}
  </p>
));

const Bubble = ({ m, idx, sid, onAsk, onCart, isLast = false, d, lastBooking, sync }) => (
  <div className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : ''}`}>
    {m.role !== 'user' && <AIAvatar d={d} />}
    <div className={`max-w-[85%] space-y-2 ${m.role === 'user' ? 'items-end' : ''}`}>
      {m.page_product?.name && <div className="text-[10px] text-sky-700 text-right" data-testid="ai-msg-product-tag">zu: {m.page_product.name}</div>}
      {(m.content || m.pending) && (
        <div className={`rounded-2xl px-3 py-2 text-sm leading-relaxed space-y-0.5 ${m.role === 'user' ? 'text-white rounded-br-md' : 'bg-white border border-neutral-200 text-neutral-800 rounded-bl-md'}`} style={m.role === 'user' ? { background: d?.accent_color || '#0c4a6e' } : undefined}>
          {m.content ? md(m.content, m.role === 'user' ? null : (m.cards || []).filter((c) => c.kind === 'products').flatMap((c) => (c.products || []).map((p) => p.name))) : <span className="inline-flex gap-1 py-1">{[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-bounce" style={{ animationDelay: `${i * 120}ms` }} />)}</span>}
        </div>
      )}
      {(m.cards || []).map((c, i) => (
        <div key={i}>
          {c.kind === 'products' && <ProductCards products={c.products} sid={sid} onAsk={onAsk} />}
          {c.kind === 'extras' && <ExtrasCard card={c} sid={sid} onAsk={onAsk} />}
          {c.kind === 'booking' && <BookingCard card={c} sid={sid} onCart={onCart} cardKey={`${sid}-${idx}-${i}`} active={lastBooking === `${idx}-${i}`} sync={lastBooking === `${idx}-${i}` ? sync : null} />}
          {c.kind === 'handoff' && <HandoffCard sid={sid} cardKey={`${sid}-${idx}-${i}`} />}
          {c.kind === 'replies' && isLast && (
            <div className="flex flex-wrap gap-1.5" data-testid="ai-reply-options">
              {c.options.map((o, j) => (
                <button key={o} type="button" onClick={() => onAsk(o)} className="rounded-full border border-sky-300 bg-white px-3 py-1 text-xs font-semibold text-sky-800 hover:bg-sky-50 transition-colors" data-testid={`ai-reply-option-${j}`}>{o}</button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
    {m.role === 'user' && <span className="w-7 h-7 rounded-full bg-yellow-400 text-sky-900 flex items-center justify-center shrink-0"><UserRound className="w-4 h-4" /></span>}
  </div>
);

export default function AIChatWidget() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useCustomerAuth();
  const [showHistory, setShowHistory] = useState(false);
  const autoLoaded = useRef(false);
  const sendRef = useRef(null);
  const [pageProduct, setPageProduct] = useState(() => window.__kidseventAiProduct || null);
  const [dismissedPid, setDismissedPid] = useState(null);
  const ctxProduct = pageProduct && pageProduct.id !== dismissedPid ? pageProduct : null;
  const cfg = useAIConfig();
  const d = cfg?.design || {};
  const left = d.position === 'left';
  const nextSource = useRef(null);
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(() => sessionStorage.getItem(SID_KEY) || '');
  const [msgs, setMsgs] = useState(() => { try { return JSON.parse(sessionStorage.getItem(MSG_KEY) || '[]'); } catch { return []; } });
  const bookingState = useMemo(() => {
    let last = null; let sync = null;
    msgs.forEach((m, i) => (m.cards || []).forEach((c, j) => { if (c.kind === 'booking') last = `${i}-${j}`; if (c.kind === 'booking_sync') sync = c; }));
    return { last, sync };
  }, [msgs]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const [cookieOk, setCookieOk] = useState(() => { try { return !!localStorage.getItem('cookie-consent-v1'); } catch { return true; } });

  useEffect(() => {
    const ok = () => setCookieOk(true);
    window.addEventListener('cookie-consent-accepted', ok);
    return () => window.removeEventListener('cookie-consent-accepted', ok);
  }, []);

  const clearLocal = useCallback(() => {
    sessionStorage.removeItem(SID_KEY); sessionStorage.removeItem(MSG_KEY); clearDrafts();
    setSid(''); setMsgs([]);
  }, []);

  const loadChat = useCallback(async (id) => {
    const r = await fetch(`${API}/api/ai/session/${id}`, { credentials: 'include' }).catch(() => null);
    if (!r?.ok) {
      toast.error('Dieser Chat ist nicht mehr verfügbar.');
      window.dispatchEvent(new Event('kidsevent-ai-changed'));
      return;
    }
    const d = await r.json();
    sessionStorage.setItem(SID_KEY, d.id);
    setSid(d.id); setMsgs(d.messages || []); setShowHistory(false);
  }, []);

  useEffect(() => {
    if (!user) {
      if (autoLoaded.current) clearLocal();
      autoLoaded.current = false;
      return;
    }
    if (autoLoaded.current) return;
    autoLoaded.current = true;
    (async () => {
      const cur = sessionStorage.getItem(SID_KEY);
      if (cur) {
        await fetch(`${API}/api/me/ai-chats/claim`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ session_id: cur }) }).catch(() => {});
        return;
      }
      const r = await fetch(`${API}/api/me/ai-chats`, { credentials: 'include' }).catch(() => null);
      const first = r?.ok ? (await r.json()).items?.[0] : null;
      if (first) loadChat(first.id);
    })();
  }, [user, loadChat, clearLocal]);

  useEffect(() => {
    const onOpen = (e) => { setOpen(true); if (e.detail?.sid) loadChat(e.detail.sid); };
    const onDel = (e) => { if (sessionStorage.getItem(SID_KEY) === e.detail?.sid) clearLocal(); };
    window.addEventListener('kidsevent-ai-open', onOpen);
    window.addEventListener('kidsevent-ai-deleted', onDel);
    return () => { window.removeEventListener('kidsevent-ai-open', onOpen); window.removeEventListener('kidsevent-ai-deleted', onDel); };
  }, [loadChat, clearLocal]);

  useEffect(() => {
    const onCtx = (e) => setPageProduct(e.detail || null);
    const onAsk = (e) => { setOpen(true); setShowHistory(false); if (e.detail?.text) { nextSource.current = e.detail.source || null; setTimeout(() => sendRef.current?.(e.detail.text), 50); } };
    window.addEventListener('kidsevent-ai-context', onCtx);
    window.addEventListener('kidsevent-ai-ask', onAsk);
    return () => { window.removeEventListener('kidsevent-ai-context', onCtx); window.removeEventListener('kidsevent-ai-ask', onAsk); };
  }, []);

  useEffect(() => { sessionStorage.setItem(MSG_KEY, JSON.stringify(msgs.filter((m) => !m.pending))); endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, open]);

  if (!cfg?.enabled || loc.pathname.startsWith('/admin') || loc.pathname.startsWith('/fahrer')) return null;

  const patchLast = (fn) => setMsgs((all) => { const c = [...all]; c[c.length - 1] = fn(c[c.length - 1]); return c; });

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || busy) return;
    setInput('');
    setBusy(true);
    const source = nextSource.current || (ctxProduct ? 'product' : 'widget');
    nextSource.current = null;
    setMsgs((m) => [...m, { role: 'user', content: t, page_product: ctxProduct || undefined }, { role: 'assistant', content: '', cards: [], pending: true }]);
    try {
      const res = await fetch(`${API}/api/ai/chat`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid, message: t, page_product_id: ctxProduct?.id || null, source }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Der Assistent ist gerade nicht erreichbar.');
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith('data: ')) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === 'delta') patchLast((m) => ({ ...m, content: m.content + ev.text }));
          if (ev.type === 'reset') patchLast((m) => ({ ...m, content: '' }));
          else if (ev.type === 'card') patchLast((m) => ({ ...m, cards: [...(m.cards || []), ev.card] }));
          else if (ev.type === 'error') patchLast((m) => ({ ...m, content: m.content || ev.text }));
          else if (ev.type === 'done' && ev.session_id) { setSid(ev.session_id); sessionStorage.setItem(SID_KEY, ev.session_id); }
        }
      }
    } catch (e) {
      patchLast((m) => ({ ...m, content: e.message }));
    } finally {
      patchLast((m) => ({ ...m, pending: false }));
      setBusy(false);
      window.dispatchEvent(new Event('kidsevent-ai-changed'));
    }
  };

  sendRef.current = send;

  const toCart = (products, from, to) => {
    products.forEach((p) => {
      const days = from && to ? Math.max(1, Math.round((new Date(to) - new Date(from)) / 86400000) + 1) : 1;
      addToCart({ ...p, price_1_day: p.price_1_day }, days, from || null, to && to !== from ? to : null, { quantity: 1 });
    });
    setOpen(false);
    navigate('/mietanfrage');
  };

  const reset = async () => {
    if (sid && !window.confirm('Diesen Chat-Verlauf löschen?')) return;
    if (sid) await fetch(`${API}/api/ai/session/${sid}`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
    clearLocal(); setShowHistory(false);
    window.dispatchEvent(new Event('kidsevent-ai-changed'));
  };

  return (
    <>
      {!open && cookieOk && (
        <div className={`md:hidden fixed z-[60] ${left ? 'left-3' : 'right-3'} ai-fab-mobile`}>
          <AIFab size="w-14 h-14" testid="ai-chat-fab" mobile />
        </div>
      )}
      {!open && cookieOk && left && (
        <div className="hidden md:block fixed z-50 left-4 bottom-[76px]">
          <AIFab testid="ai-fab-left" />
        </div>
      )}
      {open && (
        <div className="md:hidden fixed inset-0 z-[65] bg-black/40 animate-in fade-in duration-200" onClick={() => setOpen(false)} data-testid="ai-chat-backdrop" />
      )}
      {open && (
        <div className={`fixed z-[70] inset-x-3 bottom-3 h-[80dvh] max-h-[680px] md:inset-auto ${left ? 'md:left-4 origin-bottom-left' : 'md:right-4 origin-bottom-right'} md:bottom-4 md:w-[400px] md:h-[640px] md:max-h-[85vh] bg-neutral-50 rounded-2xl shadow-2xl border border-neutral-200 flex flex-col overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-4 fade-in duration-200`}
          data-testid="ai-chat-panel">
          <div className="flex items-center gap-2 px-4 py-3 text-white" style={{ background: d.header_color || '#075985' }} data-testid="ai-chat-header">
            {d.avatar_url ? <AIAvatar d={d} className="w-8 h-8" /> : <Bot className="w-5 h-5" />}
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm leading-tight flex items-center gap-1.5" data-testid="ai-chat-name">{d.name || 'KidsEvent Assistent'}
              </div>
              {d.subtitle !== '' && <div className="text-[11px] text-white/75 truncate" data-testid="ai-chat-subtitle">{d.subtitle || 'Digitaler KI-Assistent · antwortet sofort'}</div>}
            </div>
            {user && (
              <>
                <button type="button" onClick={() => setShowHistory((v) => !v)} title="Frühere Chats" className={`p-1.5 rounded hover:bg-white/10 ${showHistory ? 'bg-white/15' : ''}`} data-testid="ai-chat-history-toggle"><History className="w-4 h-4" /></button>
                <button type="button" onClick={() => { clearLocal(); setShowHistory(false); }} title="Neuer Chat" className="p-1.5 rounded hover:bg-white/10" data-testid="ai-chat-new"><Plus className="w-4 h-4" /></button>
              </>
            )}
            <button type="button" onClick={reset} title="Verlauf löschen" className="p-1.5 rounded hover:bg-white/10" data-testid="ai-chat-reset"><Trash2 className="w-4 h-4" /></button>
            <button type="button" onClick={() => setOpen(false)} title="Minimieren" className="p-1.5 rounded hover:bg-white/10" data-testid="ai-chat-minimize"><Minus className="w-4 h-4" /></button>
            <button type="button" onClick={() => { setOpen(false); }} title="Schließen" className="p-1.5 rounded hover:bg-white/10" data-testid="ai-chat-close"><X className="w-4 h-4" /></button>
          </div>
          {showHistory && user && (
            <div className="flex-1 overflow-y-auto bg-white" data-testid="ai-chat-history-panel">
              <div className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-neutral-500 border-b">Frühere Chats</div>
              <ChatHistoryList compact onOpen={loadChat} activeSid={sid} />
            </div>
          )}
          <div className={`flex-1 overflow-y-auto p-3 space-y-3 ${showHistory && user ? 'hidden' : ''}`} data-testid="ai-chat-messages">
            <Bubble d={d} m={{ role: 'assistant', content: cfg.greeting }} sid={sid} onAsk={send} onCart={toCart} />
            {msgs.length === 0 && !ctxProduct && (
              <div className="flex flex-wrap gap-1.5 pl-9" data-testid="ai-quick-replies">
                {(cfg.quick_replies || []).map((q) => (
                  <button key={q} type="button" onClick={() => send(q)} className="rounded-full border border-sky-200 bg-white px-3 py-1.5 text-xs font-semibold text-sky-900 hover:bg-sky-50">{q}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => <Bubble d={d} key={i} idx={i} m={m} sid={sid} onAsk={send} onCart={toCart} isLast={i === msgs.length - 1 && !busy} lastBooking={bookingState.last} sync={bookingState.sync} />)}
            <div ref={endRef} />
          </div>
          {ctxProduct && <ProductContextBar product={ctxProduct} cfg={cfg} busy={busy} showChips={!msgs.some((m) => m.role === 'user' && m.page_product?.id === ctxProduct.id)} onAsk={send} onDismiss={() => setDismissedPid(ctxProduct.id)} />}
          <div className="border-t bg-white p-2.5">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Schreib deine Frage…" maxLength={1500}
                className="flex-1 rounded-full border border-neutral-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-300" data-testid="ai-chat-input" />
              <button type="submit" disabled={busy || !input.trim()} className="h-10 w-10 rounded-full bg-yellow-400 text-sky-900 flex items-center justify-center disabled:opacity-40" data-testid="ai-chat-send">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
            <div className="flex justify-between mt-1.5 px-1 text-[10px] text-neutral-400">
              <span data-testid="ai-chat-footer-note">{d.footer_note ?? 'KI-Antworten können Fehler enthalten.'} <a href="/datenschutz" className="underline">Datenschutz</a></span>
              <span className="flex gap-2">
                <button type="button" onClick={() => window.dispatchEvent(new Event('kidsevent-open-inquiry'))} className="underline" data-testid="ai-chat-inquiry-form">Kontaktformular</button>
                <button type="button" onClick={() => send('Ich möchte gern mit eurem Team sprechen.')} className="underline" data-testid="ai-chat-team">Team kontaktieren</button>
              </span>
            </div>
            <ChatContacts d={d} />
          </div>
        </div>
      )}
    </>
  );
}
